import React, { useState } from 'react';
import { InfoIcon } from 'lucide-react';

// Separate LoadingSpinner component
export const LoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <div className="w-5 h-5 border-b-2 border-white rounded-full animate-spin"></div>
  </div>
);

// Skeleton loader with shimmer effect
const SkeletonText = ({ className }) => (
  <div className={`relative overflow-hidden rounded ${className}`}>
    <div className="absolute inset-0 bg-gray-200 dark:bg-white/10"></div>
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-gray-300 dark:via-white/20 to-transparent"></div>
  </div>
);

export const BalanceCard = ({ 
  icon, 
  title, 
  balance, 
  valueUSD, 
  isLoading, 
  isFiat = false,
  extraDetails = null,
  tooltipText,
  error = null,
  onRetry = null
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Calculate the minimum height needed based on content
  const getContentHeight = () => {
    let height = isFiat ? 40 : 80; // Base height for USD only or USD+BTC
    if (extraDetails) height += 100; // Additional height for extra details
    if (error) height += 40; // Additional height for error message
    return height;
  };

  return (
    <div className="p-6 transition-all duration-500 bg-white dark:bg-white/5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/10 hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-white/5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 transition-colors duration-500 dark:text-white">{title}</h3>
        <div className="flex items-center gap-2">
          {tooltipText && (
            <div className="relative">
              <button 
                className="text-gray-600 transition-colors duration-500 dark:text-white/60 hover:text-gray-800 dark:hover:text-white/90"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <InfoIcon className="w-4 h-4" />
              </button>
              
              {showTooltip && (
                <div className="absolute right-0 z-10 w-64 p-3 -mt-2 text-xs text-white transform translate-y-full bg-gray-800 rounded-md shadow-lg">
                  {tooltipText}
                </div>
              )}
            </div>
          )}
          {icon}
        </div>
      </div>
      
      <div style={{ minHeight: `${getContentHeight()}px` }}>
        {isLoading ? (
          <div>
            <SkeletonText className="w-3/4 h-8 mb-2" />
            {!isFiat && (
              <SkeletonText className="w-5/6 h-6 mb-2" />
            )}
            {extraDetails && (
              <div className="pt-3 mt-3 border-t border-gray-200 dark:border-white/10">
                <SkeletonText className="w-full h-4 mb-2" />
                <SkeletonText className="w-full h-4 mb-2" />
                <SkeletonText className="w-full h-4" />
              </div>
            )}
          </div>
        ) : error ? (
          <div className="py-2">
            <p className="mb-2 text-sm text-red-600 dark:text-red-400">{error}</p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="px-3 py-1 text-xs text-red-800 bg-red-100 rounded dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50"
              >
                Retry
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="h-8 mb-2 text-2xl font-bold text-gray-900 transition-colors duration-500 dark:text-white">
              ${valueUSD.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </p>
            
            {!isFiat && (
              <p className="h-6 mb-2 text-lg font-bold text-gray-700 transition-colors duration-500 dark:text-white/80">
                {balance.toLocaleString(undefined, {
                  minimumFractionDigits: 8,
                  maximumFractionDigits: 8
                })} BTC
              </p>
            )}
            
            {extraDetails && (
              <div className="pt-3 mt-3 text-gray-900 transition-colors duration-300 border-t border-gray-200 dark:border-white/10 dark:text-white">
                {extraDetails}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}; 